import type { BoardColumnDef } from "@design-system/Board/Board.types";

/** A board column and whether it holds any card. */
export interface BoardColumnFill {
    id: string;
    isEmpty: boolean;
}

/** Each column and whether its items leave it empty. */
export const getBoardColumnFills = <TItem>({
    columns,
    itemsByColumnId,
}: {
    columns: readonly BoardColumnDef[];
    itemsByColumnId: ReadonlyMap<string, readonly TItem[]>;
}): BoardColumnFill[] =>
    columns.map(({ id }) => ({
        id,
        isEmpty: (itemsByColumnId.get(id)?.length ?? 0) === 0,
    }));

interface ColumnCollapseSets {
    /** Columns the user collapsed while they had cards; saved. */
    handCollapsedIds: readonly string[];
    /** Columns the user expanded while empty; held in memory for the session. */
    sessionExpandedIds: readonly string[];
}

/**
 * The ids of the columns that show collapsed, in column order: those not
 * expanded this session that are empty or collapsed by hand.
 */
export const resolveCollapsedColumnIds = ({
    columns,
    handCollapsedIds,
    sessionExpandedIds,
}: ColumnCollapseSets & {
    columns: readonly BoardColumnFill[];
}): string[] =>
    columns
        .filter(
            (column) =>
                !sessionExpandedIds.includes(column.id) &&
                (column.isEmpty || handCollapsedIds.includes(column.id)),
        )
        .map((column) => column.id);

/** The ids in only one of `collapsedColumnIds` and `nextCollapsedColumnIds`: the columns a toggle flips. */
export const getToggledColumnIds = ({
    collapsedColumnIds,
    nextCollapsedColumnIds,
}: {
    collapsedColumnIds: readonly string[];
    nextCollapsedColumnIds: readonly string[];
}): string[] => [
    ...collapsedColumnIds.filter((id) => !nextCollapsedColumnIds.includes(id)),
    ...nextCollapsedColumnIds.filter((id) => !collapsedColumnIds.includes(id)),
];

/** Whether both lists hold the same ids in the same order. */
export const haveSameIds = (
    ids: readonly string[],
    otherIds: readonly string[],
) =>
    ids.length === otherIds.length &&
    ids.every((id, index) => id === otherIds[index]);

const addId = (ids: readonly string[], id: string) =>
    ids.includes(id) ? ids : [...ids, id];

const removeId = (ids: readonly string[], id: string) =>
    ids.includes(id) ? ids.filter((otherId) => otherId !== id) : ids;

/**
 * The saved and session sets after the board reports its next collapsed ids.
 * Only flipped columns change. A column with cards writes `handCollapsedIds`
 * (a collapse also drops its session expand); an empty one writes only
 * `sessionExpandedIds`. Each output is the same array when it didn't change,
 * so callers skip the storage write.
 */
export const applyColumnCollapseToggle = ({
    columns,
    handCollapsedIds,
    sessionExpandedIds,
    collapsedColumnIds,
}: ColumnCollapseSets & {
    columns: readonly BoardColumnFill[];
    collapsedColumnIds: readonly string[];
}): ColumnCollapseSets => {
    const toggledIds = getToggledColumnIds({
        collapsedColumnIds: resolveCollapsedColumnIds({
            columns,
            handCollapsedIds,
            sessionExpandedIds,
        }),
        nextCollapsedColumnIds: collapsedColumnIds,
    });
    return columns
        .filter((column) => toggledIds.includes(column.id))
        .reduce<ColumnCollapseSets>(
            (sets, { id, isEmpty }) => {
                const isCollapsing = collapsedColumnIds.includes(id);
                if (isEmpty) {
                    return {
                        ...sets,
                        sessionExpandedIds: isCollapsing
                            ? removeId(sets.sessionExpandedIds, id)
                            : addId(sets.sessionExpandedIds, id),
                    };
                }
                return isCollapsing
                    ? {
                          handCollapsedIds: addId(sets.handCollapsedIds, id),
                          sessionExpandedIds: removeId(
                              sets.sessionExpandedIds,
                              id,
                          ),
                      }
                    : {
                          ...sets,
                          handCollapsedIds: removeId(sets.handCollapsedIds, id),
                      };
            },
            { handCollapsedIds, sessionExpandedIds },
        );
};
